import type { EventTable, EventTableKind, NewTableKind, TableKindUpdate } from "@hobbyshopify/backend-client";
import { formatAmount } from "./money";

/** A kind of table as the form holds it: every field a string an input binds to. */
export interface TableKindDraft {
  readonly name: string;
  readonly description: string;
  /** The host's note for the shop that pays for a table of the kind. */
  readonly privateDescription: string;
  /** In the shop's currency, as typed: "75" or "75.50". */
  readonly price: string;
  readonly quantity: string;
  /** The letters before each table's number (A-1, A-2); blank on a new kind takes the next letter free. */
  readonly prefix: string;
}

export type TableKindField = keyof TableKindDraft;
export type TableKindErrors = Partial<Record<TableKindField, string>>;

export const EMPTY_TABLE_KIND: TableKindDraft = {
  name: "",
  description: "",
  privateDescription: "",
  price: "",
  quantity: "1",
  prefix: "",
};

export const MAX_TABLES = 500;

/** The tables could not be read, for a reason other than a refusal. */
export const TABLES_FAILED_TO_LOAD = "The tables could not be read. Reload the page to try again.";

/** Adding, changing or removing a kind failed on its way: it may have been done all the same. */
export const TABLES_FAILED_ON_THE_WAY =
  "Something went wrong on the way. Look at the tables before trying again: the change may have been made.";

export function tableKindDraft(kind: EventTableKind): TableKindDraft {
  return {
    name: kind.name,
    description: kind.description ?? "",
    privateDescription: kind.privateDescription ?? "",
    price: (kind.priceCents / 100).toFixed(2),
    quantity: String(kind.tables.length),
    prefix: kind.prefix,
  };
}

/** "75", "75.5" or "75.50" in minor units; null for anything else. */
export function parsePrice(text: string): number | null {
  const typed = text.trim();
  if (!/^\d{1,6}(\.\d{1,2})?$/.test(typed)) return null;
  const [whole, fraction = ""] = typed.split(".");
  return Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
}

export function takenCount(kind: EventTableKind): number {
  return kind.tables.filter((table) => table.status === "SOLD").length;
}

/** Tables a shop is paying for right now. */
export function payingCount(kind: EventTableKind): number {
  return kind.tables.filter((table) => table.status === "HELD").length;
}

/** The kind's tables shops hold. */
export function takenTables(kind: EventTableKind): EventTable[] {
  return kind.tables.filter((table) => table.status === "SOLD");
}

/**
 * Who holds a taken table and what became of its payment: "Rookie Row · $81.31 paid", or
 * "Reserved for jane@x.com · $81.31 paid" until a shop redeems the reservation.
 */
export function takenLine(table: EventTable): string {
  const reservedFor = table.order?.reservedFor;
  const shop = reservedFor ? `Reserved for ${reservedFor}` : (table.booth?.store.name ?? "A shop");
  const order = table.order;
  if (!order) return shop;
  if (order.amountCents === 0) return `${shop} · free`;
  const amount = formatAmount(order.amountCents, order.currency);
  if (order.status === "REFUNDED") return `${shop} · ${amount} refunded`;
  if (order.status === "FAILED") return `${shop} · ${amount} owed back`;
  return `${shop} · ${amount} paid`;
}

/** Whether the host may take a taken table back: its payment still stands. */
export function mayTakeBack(table: EventTable): boolean {
  return table.status === "SOLD" && (table.order === null || table.order.status === "PAID");
}

/** Taken tables at the show whose payment still stands. */
export function paidTables(kinds: readonly EventTableKind[]): number {
  return kinds.reduce((sum, kind) => sum + takenTables(kind).filter((t) => t.order?.status === "PAID").length, 0);
}

/** The form's own checks; `taken` is how many tables of the kind shops hold or are paying for. */
export function validateTableKindDraft(draft: TableKindDraft, taken = 0): TableKindErrors {
  const errors: TableKindErrors = {};
  if (!draft.name.trim()) errors.name = "Name the kind of table.";
  if (parsePrice(draft.price) === null) errors.price = "Enter a price, like 75 or 75.50.";
  const prefix = draft.prefix.trim();
  if (prefix && !/^[A-Za-z]{1,3}$/.test(prefix)) errors.prefix = "Use one to three letters, like A or VIP.";
  const quantity = draft.quantity.trim();
  const count = Number(quantity);
  if (!/^\d+$/.test(quantity) || count < 1 || count > MAX_TABLES) {
    errors.quantity = `Enter how many, from 1 to ${MAX_TABLES}.`;
  } else if (count < taken) {
    errors.quantity = `Shops hold or are paying for ${taken} of these. Keep at least that many.`;
  }
  return errors;
}

export function draftToNewTableKind(draft: TableKindDraft): NewTableKind {
  const description = draft.description.trim();
  const privateDescription = draft.privateDescription.trim();
  return {
    name: draft.name.trim(),
    ...(description ? { description } : {}),
    ...(privateDescription ? { privateDescription } : {}),
    priceCents: parsePrice(draft.price) ?? 0,
    quantity: Number(draft.quantity.trim()),
    ...(draft.prefix.trim() ? { prefix: draft.prefix.trim().toUpperCase() } : {}),
  };
}

/**
 * An edit sends only the fields the host changed in the form (`changed`), as it holds them now: the server
 * may hold newer values of the rest. A blank description or note clears it.
 */
export function draftToTableKindUpdate(draft: TableKindDraft, changed: ReadonlySet<TableKindField>): TableKindUpdate {
  return {
    ...(changed.has("name") ? { name: draft.name.trim() } : {}),
    ...(changed.has("description") ? { description: draft.description.trim() || null } : {}),
    ...(changed.has("privateDescription") ? { privateDescription: draft.privateDescription.trim() || null } : {}),
    ...(changed.has("price") ? { priceCents: parsePrice(draft.price) ?? 0 } : {}),
    ...(changed.has("quantity") ? { quantity: Number(draft.quantity.trim()) } : {}),
    ...(changed.has("prefix") && draft.prefix.trim() ? { prefix: draft.prefix.trim().toUpperCase() } : {}),
  };
}

/** A kind's table labels as runs: "A-1–A-3, A-6". */
export function tableLabels(kind: EventTableKind): string {
  const runs: string[] = [];
  const numbers = kind.tables.map((table) => Number(table.label.slice(kind.prefix.length + 1))).sort((a, b) => a - b);
  const label = (n: number) => `${kind.prefix}-${n}`;
  for (let i = 0; i < numbers.length; ) {
    let j = i;
    while (j + 1 < numbers.length && numbers[j + 1] === numbers[j] + 1) j += 1;
    runs.push(i === j ? label(numbers[i]) : `${label(numbers[i])}–${label(numbers[j])}`);
    i = j + 1;
  }
  return runs.join(", ");
}
